import { useModal } from '@/components/ModalContext';
import { SuccessModal } from '@lib/sc-eone-ui/src/modal/success';
import { ConfirmationModal } from 'eone-ui/common-modals/confirmation-modal';
import { ErrorModal } from 'eone-ui/common-modals/error-modal';
import { getDataTestId } from 'eone-ui/functions/data-test-ids';
import type { ModalButtonType } from 'eone-ui/modal/modal-constants';
import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';

type ConfirmationModalProps = {
  Title?: string;
  Message?: string;
  ConfirmLabel?: string;
  CancelLabel?: string;
  ConfirmType?: ModalButtonType;
  Data?: any;
  DataTestId?: string;
};

type ErrorModalProps = {
  Title?: string;
  /** SimpleModalMessage interpolates this directly, so a node renders as-is. */
  Error?: { description: ReactNode };
  DataTestId?: string;
};

type SuccessModalProps = {
  Title?: string;
  Message?: string;
  ButtonLabel?: string;
  ButtonType?: ModalButtonType;
  DataTestId?: string;
};

type CancelRunModalProps = {
  Title?: string;
  Message?: string;
  DataTestId?: string;
};

// SC-664: cancel confirmation with an optional free-text reason.
// Resolves with the reason string (possibly empty) when confirmed, or undefined when dismissed.
function CancelRunReasonModal({
  Title,
  Message,
  DataTestId,
  onConfirm,
  onCancel,
}: CancelRunModalProps & { onConfirm: (reason: string) => void; onCancel: () => void }) {
  const [reason, setReason] = useState('');
  const testId = DataTestId ?? 'integrations.current-runs.cancel-modal';
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onCancel}
    >
      <div
        style={{
          width: 460,
          maxWidth: '90vw',
          backgroundColor: 'var(--surface-base-default)',
          color: 'var(--text-primary)',
          borderRadius: 8,
          boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
          padding: 24,
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid={getDataTestId(testId, 'container')}
      >
        <h2 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 600 }}>
          {Title ?? 'Cancel Confirmation'}
        </h2>
        <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>
          {Message ?? 'Are you sure you want to cancel this run?'}
        </div>
        <label
          style={{
            fontSize: 13,
            color: 'var(--text-secondary)',
            display: 'block',
            marginBottom: 6,
          }}
        >
          Reason (optional)
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="e.g. wrong source file, taking too long…"
          autoFocus
          style={{
            width: '100%',
            boxSizing: 'border-box',
            resize: 'vertical',
            padding: 8,
            borderRadius: 4,
            border: '1px solid var(--border-subtle)',
            backgroundColor: 'var(--surface-subtle-default)',
            color: 'var(--text-primary)',
            fontSize: 13,
          }}
          data-testid={getDataTestId(testId, 'reason-input')}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              borderRadius: 4,
              border: '1px solid var(--border-subtle)',
              background: 'var(--surface-base-default)',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              fontSize: 13,
            }}
            data-testid={getDataTestId(testId, 'dismiss')}
          >
            Keep Running
          </button>
          <button
            onClick={() => onConfirm(reason.trim())}
            style={{
              padding: '8px 16px',
              borderRadius: 4,
              border: 'none',
              background: 'var(--button-danger-default, #d64545)',
              color: '#fff',
              cursor: 'pointer',
              fontSize: 13,
            }}
            data-testid={getDataTestId(testId, 'confirm')}
          >
            Cancel Run
          </button>
        </div>
      </div>
    </div>
  );
}

function useHintModals() {
  const { show } = useModal();

  const showConfirmationModal = useCallback(async (props: ConfirmationModalProps) => {
    const res = await show<boolean>(({ close, result }) => (
      <ConfirmationModal
        {...props}
        Show
        OnConfirm={() => result(props.Data ?? true)}
        Close={close}
      />
    ));
    return res;
  }, []);

  const showErrorModal = useCallback(async (props: ErrorModalProps) => {
    const res = await show<boolean>(({ close }) => <ErrorModal {...props} Show OnCancel={close} />);
    return res;
  }, []);

  const showSuccessModal = useCallback(async (props: SuccessModalProps) => {
    const res = await show<boolean>(({ close }) => <SuccessModal {...props} Show Close={close} />);
    return res;
  }, []);

  // SC-664: returns the reason string (possibly empty) if the user confirmed cancellation,
  // or undefined if they dismissed without cancelling.
  const showCancelRunModal = useCallback(
    async (props: CancelRunModalProps) => {
      return await show<string>(({ close, result }) => (
        <CancelRunReasonModal {...props} onConfirm={(reason) => result(reason)} onCancel={close} />
      ));
    },
    [show],
  );

  return {
    showConfirmationModal,
    showErrorModal,
    showSuccessModal,
    showCancelRunModal,
  };
}

export default useHintModals;
